import * as Yup from 'yup';
import { getRequiredText } from '../../../../../../global/helpers/transformer';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { MaxCharacter255 } from '../../../../constants/constant-strings';
import { AnnualEnrollmentProps, NewHireProps } from '../../../../models/arc-epi-props';

export const declineCoverageReasonOther = 'other';

export enum TiersTypes {
	Family = 'F',
	EmpPlusOne = 'T',
	Single = 'I',
	EmpPlusChild = 'G',
	EmpPlusMulti = 'D',
}

export enum RelationshipCode {
	Child = 'CHD',
	StepChild = 'STP',
	Spouse = 'SPS',
	DomesticPartner = 'DP',
	Disabled = 'H',
}


export interface Tiers {
	D: number;
	F: number;
	G: number;
	I: number;
	T: number;
}

export enum BillingPrefernce {
	ALLOWED = 'allowed',
	REQUIRED = 'required',
	DISALLOWED = 'disallowed',
}
export interface CoveragePlans {
	account_number: string;
	effective_date: string;
	group_id: string;
	plans: CoveragePlan[];
	diocese_account_number: string;
	billing_preference: BillingPrefernce;
	selected: SelectedPlan
	current?: CurrentPlanSelections;
	member_selection_required?: boolean;
}

export interface CurrentPlanSelections {
	code?: string;
	tier?: string;
	effective_date: string;
	dependents?: string[];
	decline_reason?: string;
	pending_plan_selection?: string;
	volume?: number | null;
	account_number?: string;
	group_id?: string;
	policy_number?: string;
	rate?: string;
	name?: string;
}

export interface CoveragePlan {
	code: string;
	name: string;
	pdf: string;
	rates_by_tier?: Tiers,
	rate?: number;
	volume?: number;
}

export interface CoveragePerson {
	guid: string;
	disabled_child_indicator: YesNoIndicators;
	first_name: string;
	middle_name: string;
	last_name: string;
	suffix: string;
	relationship: RelationshipCode,
	relationship_start_date: string;
	age: number;
	remove?: boolean;
}

export enum PlanTypes {
	M = 'M',
	D = 'D',
	G = 'G',
	W = 'W',
	L = 'L',
	U = 'U',
	None = 'None',
}

export enum TierMessage {
	F = 'Family',
	T = 'EmpPlusOne',
	I = 'Single',
	G = 'EmpPlusChild',
	D = 'EmpPlusMulti',
	N = 'NA',
}

export enum DisplayLabels {
	MEDICALDENTAL = 'MEDICAL&DENTAL',
	VOLUME = 'VOLUME',
	RATE = 'RATE',
	VOLUMERATE = 'VOLUMERATE',
}

export interface VolumeRateValuesGroupLifeProps {
	rate: number;
	volume: number;
}

export const DeclinedValue = 'Declined';

export interface CoverageFormModel {
	[PlanTypes.M]: string;
	[PlanTypes.D]: string;
	[PlanTypes.G]: string;
	[PlanTypes.W]: string;
	[PlanTypes.L]: string;
	[PlanTypes.U]: string;
	M_billingAccountType: string;
	D_billingAccountType: string;
	G_billingAccountType: string;
	W_billingAccountType: string;
	L_billingAccountType: string;
	U_billingAccountType: string;
	medicalDependents: string[];
	dentalDependents: string[];
	agreement: boolean;

	medicalDeclineReason: string;
	medicalDeclineReasonOther: string;
	dentalDeclineReason: string;
	dentalDeclineReasonOther: string;

	M_member?: boolean;
	D_member?: boolean;

	W_member?: boolean;
	L_member?: boolean;

	memo?: string;
	specialRequests?: string;
	cafeteriaAcknowledgement?: boolean;
}

export interface PlanSelectionComponentModel {
	formPlanTypeName: PlanTypes;
	planTypeName: string;
	planNamesLabel: string;
	settings: NewHireProps;
	labelSettings: LabelSettingProps;
	declineOptionText: string;
}

export interface FormInfo {
	change_id: string;
	change_id_signature: string;
}

export interface NewHireCoverageState {
	eligible_plans: {
		dental: CoveragePlans,
		medical: CoveragePlans,
		group_life: CoveragePlans,
		short_term_disability: CoveragePlans & { employer_paid: boolean },
		long_term_disability: CoveragePlans & { employer_paid: boolean },
		uei: CoveragePlans & { employer_paid: boolean },
	},
	eligible_dependents: CoveragePerson[],
	form_info: { [key: string]: FormInfo },
	submitted_by: {
		name: string,
		business_email: string,
		business_phone: string,
	},
	special_requests?: string;
	notes?: string;
	ghl_form_id?: number;
	employer_id?: string;
	effective_date?: string;
}

interface SelectedPlan {
	code?: string;
	tier?: string;
	billing_account_number: string;
	effective_date: string;
	dependents?: string[];
	decline_reason?: string;
	pending_plan_selection?: string;
	volume?: number | null;
	account_number?: string;
	group_id?: string;
	policy_number?: string;
	rate?: string;
}

export interface CoverageValidationSchemaModel {
	hasMedical: boolean,
	hasDental: boolean,
	hasGroup: boolean,
	hasLtd: boolean,
	hasStd: boolean,
	hasUei: boolean,
}

export interface MemberSelectionRequiredValidationSchemaModel {
	memberWillSelectMedical: boolean,
	memberWillSelectDental: boolean,
	memberWillSelectLtd: boolean,
	memberWillSelectStd: boolean,
}

export interface DioApproverInitialValues {
	selectedMedicalPlan: string;
	selectedDentalPlan: string;
	selectedGroupPlan: string;
	selectedLtdPlan: string;
	selectedStdPlan: string;
	selectedUeiPlan: string;
	selectedMedicalBillingAccountNumber: string;
	selectedDentalBillingAccountNumber: string;
	selectedGroupBillingAccountNumber: string;
	selectedLtdBillingAccountNumber: string;
	selectedStdBillingAccountNumber: string;
	selectedUeiBillingAccountNumber: string;
	selectedMedicalTier: string,
	selectedDentalTier: string,
	medicalDependents: string[] | [];
	dentalDependents: string[] | [];
	medicalDeclineReason: string | null;
	medicalDeclineReasonOther: string | null;
	dentalDeclineReason: string | null;
	dentalDeclineReasonOther: string | null;
	specialRequests: string | null;
	volume?: number
}


export function GetNewHireCoverageValidationSchema(memberSelectionValidationModel: MemberSelectionRequiredValidationSchemaModel, aeSettings: AnnualEnrollmentProps, settings: NewHireProps, labelSettings: LabelSettingProps, hasMedical: boolean, validationModel: CoverageValidationSchemaModel, groupLifePlans: CoveragePlans = null, institutionOfficerOnly: boolean = false, plansAreBeingRenderedToDioApprover: boolean = true) {

	let defaultShape: any = {
		memo: Yup.string().nullable().max(255, aeSettings?.maxCharacterErrorText || MaxCharacter255),
		specialRequests: Yup.string().nullable().max(255, aeSettings?.maxCharacterErrorText || MaxCharacter255),
	};

	if (!memberSelectionValidationModel?.memberWillSelectMedical && hasMedical && plansAreBeingRenderedToDioApprover) {
		defaultShape = {
			...defaultShape,
			medicalDeclineReason: Yup.string().nullable().ensure()
				.when(['M'], {
					is: (M: string) => {
						return  M === DeclinedValue;
					},
					then: Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.declineCoverageReasonSelectLabel)),
				}),
			medicalDeclineReasonOther: Yup.string().nullable().ensure()
				.when(['medicalDeclineReason', 'M'], {
					is: (medicalDeclineReason: string[], M: string) => {
						return M === DeclinedValue && medicalDeclineReason?.length > 0 && medicalDeclineReason.includes(declineCoverageReasonOther);
					},
					then: Yup.string().nullable().required(getRequiredText(labelSettings?.requiredFormat, settings.declineCoverageReasonOtherLabel)),
				}),
		};
	}

	if (validationModel?.hasMedical && !memberSelectionValidationModel?.memberWillSelectMedical)
		defaultShape = {
			...defaultShape,
			[PlanTypes.M]:
				Yup.string().nullable().ensure()
					.when('medicalDependents', (medicalDependents, schema) => {
						return schema.test(PlanTypes.M, (value, context) => !value || (medicalDependents?.length > 0 && value === DeclinedValue) ? context.createError({ message: getRequiredText(labelSettings?.requiredFormat, settings.medicalPlanTitle) }) : true);
					}),
		};

	if (validationModel?.hasDental && !memberSelectionValidationModel?.memberWillSelectDental)
		defaultShape = {
			...defaultShape,
			[PlanTypes.D]:
				Yup.string().nullable().ensure()
					.when('dentalDependents', (dentalDependents, schema) => {
						return schema.test(PlanTypes.D, (value, context) => !value || (dentalDependents?.length > 0 && value === DeclinedValue) ? context.createError({ message: getRequiredText(labelSettings?.requiredFormat, settings.dentalPlanTitle) }) : true);
					}),
		};

	if (validationModel?.hasGroup)
		defaultShape = {
			...defaultShape,
			[PlanTypes.G]: Yup.string().nullable().test(PlanTypes.G, (value, context) => !value ? context.createError({ message: getRequiredText(labelSettings?.requiredFormat, settings.groupLifePlanTitle) }) : true),
		};

	if (validationModel?.hasStd && !memberSelectionValidationModel?.memberWillSelectStd)
		defaultShape = {
			...defaultShape,
			[PlanTypes.W]: Yup.string().nullable().test(PlanTypes.W, (value, context) => !value ? context.createError({ message: getRequiredText(labelSettings?.requiredFormat, settings.shortDisPlanTitle) }) : true),
		};

	if (validationModel?.hasLtd && !memberSelectionValidationModel?.memberWillSelectLtd)
		defaultShape = {
			...defaultShape,
			[PlanTypes.L]: Yup.string().nullable().test(PlanTypes.L, (value, context) => !value ? context.createError({ message: getRequiredText(labelSettings?.requiredFormat, settings.longDisPlanTitle) }) : true),
		};

	if (validationModel?.hasUei)
		defaultShape = {
			...defaultShape,
			[PlanTypes.U]: Yup.string().nullable().test(PlanTypes.U, (value, context) => !value ? context.createError({ message: getRequiredText(labelSettings?.requiredFormat, settings.ueiPlanTitle) }) : true),
		};

	groupLifePlans?.plans.forEach(element => {
		if (!element.volume && !institutionOfficerOnly) {
			defaultShape[`volume${element.code}`] = Yup.number()
				.when(['G'], {
					is: (G: string) => {
						return G === element.code;
					},
					then: Yup.number().required(getRequiredText(labelSettings?.requiredFormat, settings.volumeFieldLabel))
						.positive(settings.groupLifeVolumeMaxDigitsValidationLabel)
						.max(9999999, settings.groupLifeVolumeMaxDigitsValidationLabel)
						.test('decimal', settings.groupLifeVolumeMaxDecimalDigitsValidationLabel, value => {
							if (value !== undefined && value !== null) {
								const decimalPart = value.toString().split('.')[1];
								return !decimalPart || decimalPart.length <= 2;
							}
							return true;
						}),
				});
		}

	});

	return Yup.object().shape(defaultShape);
}

GetNewHireCoverageValidationSchema.defaultProps = {
	groupLifePlans: null,
};



